import { JPBReferenceProperty, JPBAssumptions, DEFAULT_ASSUMPTIONS, AreaUnit, PriceMode, StatisticsMethod, SoilQualityGroup, SOIL_QUALITY_GROUPS, SOIL_INDEX, AverageCalculationMethod, RetentionMarkPriceBandKey, RetentionMarkPriceAdjustments, JPB_MARK_SHARE_COLUMNS } from "./JPBState";
import { SettingsManager, Localization } from "@viamap/viamap2-common";
import {
   LegacySoilQualityFactors,
   normalizeSoilQualityFactors,
   readCustomerAndUserSoilQualityFactorOverrides,
} from "./jpbSoilSettingsCompat";

export function formatCurrency(value: number, forceFractionDigits?: number): string {
   if (value === 0 || !value) return "-";
   const minFractionDigits = forceFractionDigits ?? 0;
   const maxFractionDigits = forceFractionDigits ?? 1;
   return new Intl.NumberFormat(Localization.getLocale(), {
      style: "currency",
      currency: "DKK",
      minimumFractionDigits: minFractionDigits,
      maximumFractionDigits: maxFractionDigits,
      useGrouping: true
   }).format(value);
}

export function formatPricePerSquareMeter(value: number, unit: AreaUnit = AreaUnit.SquareMeter, forceFractionDigits?: number, currencyShow: boolean = true): string {
   if (!value || isNaN(value)) return "-";

   const displayValue = unit === AreaUnit.Hectare ? value * 10000 : value;
   const rounded = unit === AreaUnit.Hectare ? Math.round(displayValue) : Math.round(displayValue * 100) / 100;
   let fractionDigits = unit === AreaUnit.Hectare ? 0 : 2;
   if (forceFractionDigits !== undefined) {
      fractionDigits = forceFractionDigits;
   }

   const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true
   };

   if (currencyShow) {
      options.style = "currency";
      options.currency = "DKK";
   }

   return new Intl.NumberFormat(Localization.getLocale(), options).format(rounded);
}

export function formatArea(value: number, unit: AreaUnit = AreaUnit.SquareMeter, forceFractionDigits?: number): string {
   if (value === 0 || !value) return "-";

   const displayValue = unit === AreaUnit.Hectare ? value / 10000 : value;
   let fractionDigits = unit === AreaUnit.Hectare ? 4 : 0;
   if (forceFractionDigits !== undefined) {
      fractionDigits = forceFractionDigits;
   }

   return new Intl.NumberFormat(Localization.getLocale(), {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true
   }).format(displayValue);
}

export function formatDate(dateString: Date | string | null): string {
   if (!dateString) return "-";
   const datoFormatter = new Intl.DateTimeFormat(Localization.getLocale(), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
   })
   return datoFormatter.format(new Date(dateString || 0));
}

export function formatWholeNumber(value: number): string {
   if (value === 0 || !value) return "-";
   return new Intl.NumberFormat(Localization.getLocale(), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
   }).format(value);
}

export function formatPercentage(value: number | null | undefined): string {
   if (value === null || value === undefined || isNaN(value)) return "-";
   return new Intl.NumberFormat(Localization.getLocale(), {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      style: "percent"
   }).format(value / 100);
}

/**
 * Mirrors the UI formatting used in `JPBTable` for the "Indeksforskel" column.
 * Hides values close to zero (|v| < 0.05) and uses the localized percent style
 * via `formatPercentage`. Exported so the report can match the table 1:1.
 */
export function formatIndexDifferenceValue(value?: number | null): string {
   if (value === null || value === undefined || !Number.isFinite(value)) return "-";
   if (Math.abs(value) < 0.05) return "-";
   return formatPercentage(value);
}


export type JPBStatisticsSeries = "indexed" | "effectiveMark" | "original";

type JPBSoilFactorsByGroup = { [soilGroup in SoilQualityGroup]: number };

function isValidPositiveNumber(value: unknown): value is number {
   return typeof value === "number" && isFinite(value) && value > 0;
}

function isValidNonNegativeNumber(value: unknown): value is number {
   return typeof value === "number" && isFinite(value) && value >= 0;
}

export type JPBWeightedValue = { value: number; weight: number };

function toFinitePositiveNumber(value: unknown, fallback: number): number {
   return typeof value === "number" && isFinite(value) && value > 0 ? value : fallback;
}

function toFiniteNonNegativeNumber(value: unknown, fallback: number): number {
   return typeof value === "number" && isFinite(value) && value >= 0 ? value : fallback;
}

function toFiniteNumber(value: unknown, fallback: number): number {
   return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/** A known 0–100 share is strictly above the criteria cap. A missing share is not. */
export function shareExceedsCap(pct: number | null | undefined, cap: number): boolean {
   return typeof pct === "number" && Number.isFinite(pct) && pct > cap;
}

function getWeightForAverageMode(
   averageCalculationMethod: AverageCalculationMethod,
   weightedArea: number
): number {
   if (averageCalculationMethod === AverageCalculationMethod.EqualPerSample) {
      return 1;
   }
   return weightedArea;
}

export function getSoilQualityFactorsByGroup(assumptions?: JPBAssumptions): JPBSoilFactorsByGroup {
   const source = assumptions ?? getResolvedJPBAssumptions();
   const configured = normalizeSoilQualityFactors(source.soilQualityFactors);
   return {
      [SoilQualityGroup.JB_1_2]: configured.JB_1_2,
      [SoilQualityGroup.JB_3_4]: configured.JB_3_4,
      [SoilQualityGroup.JB_5_6]: configured.JB_5_6,
      [SoilQualityGroup.JB_7_9]: configured.JB_7_9,
      [SoilQualityGroup.JB_10]: configured.JB_10,
      [SoilQualityGroup.JB_11]: configured.JB_11,
   };
}

/** Last-resort fallback only when `jpb.fieldLayer` is missing from settings. */
export const DEFAULT_JPB_FIELD_LAYER = "Marker_2026";

type JPBSettings = {
   fieldLayer?: string;
};

export function getJPBFieldLayer(): string {
   try {
      const jpb = SettingsManager.getSystemSetting("jpb") as JPBSettings | undefined;
      const fromJpbBlock = typeof jpb?.fieldLayer === "string" ? jpb.fieldLayer.trim() : "";
      if (fromJpbBlock) return fromJpbBlock;

      const fromFlatKey = SettingsManager.getSystemSetting("jpb.fieldLayer");
      if (typeof fromFlatKey === "string" && fromFlatKey.trim()) {
         return fromFlatKey.trim();
      }
   } catch {
      // SettingsManager not initialized yet
   }
   return DEFAULT_JPB_FIELD_LAYER;
}

export function getResolvedJPBAssumptions(customAssumptions?: Partial<JPBAssumptions>  ): JPBAssumptions {
   const systemAssumptions = SettingsManager.getSystemSetting("jpb.assumptions", DEFAULT_ASSUMPTIONS);
   const overlayFactors = readCustomerAndUserSoilQualityFactorOverrides();
   const customFactors = customAssumptions?.soilQualityFactors as LegacySoilQualityFactors | undefined;
   const merged = {
      ...DEFAULT_ASSUMPTIONS,
      ...systemAssumptions,
      ...customAssumptions,
      soilQualityFactors: {
         ...DEFAULT_ASSUMPTIONS.soilQualityFactors,
         ...systemAssumptions?.soilQualityFactors,
         ...customFactors
      },
      retentionMarkPriceAdjustments: {
         ...DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments,
         ...systemAssumptions?.retentionMarkPriceAdjustments,
         ...customAssumptions?.retentionMarkPriceAdjustments
      }
   } as JPBAssumptions;

   merged.agricultureFactor = toFiniteNonNegativeNumber(
      merged.agricultureFactor,
      DEFAULT_ASSUMPTIONS.agricultureFactor
   );
   merged.nonAgricultureFactor = toFiniteNonNegativeNumber(
      merged.nonAgricultureFactor,
      DEFAULT_ASSUMPTIONS.nonAgricultureFactor
   );
   merged.areaMin = toFiniteNonNegativeNumber(
      merged.areaMin,
      DEFAULT_ASSUMPTIONS.areaMin
   );
   merged.areaMax = toFiniteNonNegativeNumber(
      merged.areaMax,
      DEFAULT_ASSUMPTIONS.areaMax
   );
   merged.effectiveMarkOverlapThreshold = toFiniteNonNegativeNumber(
      merged.effectiveMarkOverlapThreshold,
      DEFAULT_ASSUMPTIONS.effectiveMarkOverlapThreshold
   );
   merged.maxIkkeOmdriftPct = toFiniteNonNegativeNumber(
      merged.maxIkkeOmdriftPct,
      DEFAULT_ASSUMPTIONS.maxIkkeOmdriftPct
   );
   merged.maxSection3Pct = toFiniteNonNegativeNumber(
      merged.maxSection3Pct,
      DEFAULT_ASSUMPTIONS.maxSection3Pct
   );
   merged.soilQualityFactors = normalizeSoilQualityFactors({
      ...merged.soilQualityFactors,
      JB_7_9: customFactors?.JB_7_9 ?? overlayFactors.JB_7_9,
      JB_10: customFactors?.JB_10 ?? overlayFactors.JB_10,
      JB_7_10: customFactors?.JB_7_10 ?? overlayFactors.JB_7_10,
   });
   merged.retentionMarkPriceAdjustments = {
      "0_20": toFiniteNumber(merged.retentionMarkPriceAdjustments?.["0_20"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["0_20"]),
      "20_40": toFiniteNumber(merged.retentionMarkPriceAdjustments?.["20_40"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["20_40"]),
      "40_60": toFiniteNumber(merged.retentionMarkPriceAdjustments?.["40_60"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["40_60"]),
      "60_80": toFiniteNumber(merged.retentionMarkPriceAdjustments?.["60_80"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["60_80"]),
      "80_100": toFiniteNumber(merged.retentionMarkPriceAdjustments?.["80_100"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["80_100"]),
   };
   const buildingAreaOkHa = toFiniteNonNegativeNumber(merged.buildingAreaOkM2, DEFAULT_ASSUMPTIONS.buildingAreaOkM2);
   merged.buildingAreaOkM2 = buildingAreaOkHa * 10000;
   return merged;
}

export function getNonAgricultureToAgricultureRatio(assumptions?: JPBAssumptions): number {
   const source = assumptions ?? getResolvedJPBAssumptions();
   const agricultureFactor = toFinitePositiveNumber(source.agricultureFactor, DEFAULT_ASSUMPTIONS.agricultureFactor);
   const nonAgricultureFactor = toFiniteNonNegativeNumber(source.nonAgricultureFactor, DEFAULT_ASSUMPTIONS.nonAgricultureFactor);
   return nonAgricultureFactor / agricultureFactor;
}

const RETENTION_PROPERTY_CANDIDATES = [
   "ret_tot",
   "retention",
   "retention_pct",
   "retention_procent",
   "ret_pct",
   "retentionpct"
];

function getPropertyCaseInsensitive(properties: Record<string, unknown> | null | undefined, key: string): unknown {
   if (!properties) return undefined;
   if (key in properties) return properties[key];
   const lower = key.toLowerCase();
   const found = Object.keys(properties).find((candidate) => candidate.toLowerCase() === lower);
   return found !== undefined ? properties[found] : undefined;
}

function parseRetentionNumber(value: unknown): number | null {
   if (typeof value === "number" && Number.isFinite(value)) return value;
   if (typeof value !== "string") return null;
   const normalized = value.replace("%", "").replace(",", ".").trim();
   const match = normalized.match(/-?\d+(\.\d+)?/);
   if (!match) return null;
   const parsed = Number(match[0]);
   return Number.isFinite(parsed) ? parsed : null;
}

/** Reads a retention percentage from a feature. Values in 0–1 are treated as fractions. */
export function getRetentionPercentFromFeature(feature: { properties?: Record<string, unknown> } | null | undefined): number | null {
   const properties = feature?.properties ?? {};
   for (const key of RETENTION_PROPERTY_CANDIDATES) {
      const value = parseRetentionNumber(getPropertyCaseInsensitive(properties, key));
      if (value !== null) return value <= 1 ? value * 100 : value;
   }

   const fallbackKey = Object.keys(properties).find((key) => key.toLowerCase().includes("ret"));
   if (!fallbackKey) return null;

   const value = parseRetentionNumber(properties[fallbackKey]);
   if (value === null) return null;
   return value <= 1 ? value * 100 : value;
}

export function calculateAreaWeightedAverage(parts: Array<{ value: number; area: number }>): number | null {
   let weightedSum = 0;
   let totalArea = 0;
   for (const part of parts) {
      if (!(part.area > 0) || !Number.isFinite(part.area) || !Number.isFinite(part.value)) continue;
      weightedSum += part.value * part.area;
      totalArea += part.area;
   }
   if (totalArea <= 0) return null;
   return weightedSum / totalArea;
}

export type RetentionShareOnMark = {
   retentionPercent: number;
   shareOfMark: number;
   areaM2: number;
};

export function groupRetentionSharesOnMark(
   parts: Array<{ value: number; area: number }>,
   markAreaM2: number
): { averagePercent: number | null; shares: RetentionShareOnMark[]; uncoveredShareOfMark: number } {
   const averagePercent = calculateAreaWeightedAverage(parts);
   if (!(markAreaM2 > 0)) {
      return { averagePercent, shares: [], uncoveredShareOfMark: 100 };
   }

   const areaByRoundedPercent = new Map<number, number>();
   let coveredArea = 0;
   for (const part of parts) {
      if (!(part.area > 0) || !Number.isFinite(part.area) || !Number.isFinite(part.value)) continue;
      const rounded = Math.round(part.value);
      areaByRoundedPercent.set(rounded, (areaByRoundedPercent.get(rounded) ?? 0) + part.area);
      coveredArea += part.area;
   }

   const shares = [...areaByRoundedPercent.entries()]
      .map(([retentionPercent, areaM2]) => ({
         retentionPercent,
         areaM2,
         shareOfMark: (areaM2 / markAreaM2) * 100
      }))
      .sort((a, b) => b.shareOfMark - a.shareOfMark || b.retentionPercent - a.retentionPercent);

   const uncoveredShareOfMark = Math.max(0, (1 - Math.min(coveredArea, markAreaM2) / markAreaM2) * 100);
   return { averagePercent, shares, uncoveredShareOfMark };
}

/** Static bands: [0,20), [20,40), [40,60), [60,80), [80,100]. */
export function getRetentionMarkPriceBandKey(averageRetentionPercent: number): RetentionMarkPriceBandKey {
   const pct = Math.min(100, Math.max(0, averageRetentionPercent));
   if (pct >= 80) return "80_100";
   if (pct >= 60) return "60_80";
   if (pct >= 40) return "40_60";
   if (pct >= 20) return "20_40";
   return "0_20";
}

export function getRetentionMarkPriceAdjustments(assumptions?: JPBAssumptions): RetentionMarkPriceAdjustments {
   const source = assumptions ?? getResolvedJPBAssumptions();
   return {
      "0_20": toFiniteNumber(source.retentionMarkPriceAdjustments?.["0_20"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["0_20"]),
      "20_40": toFiniteNumber(source.retentionMarkPriceAdjustments?.["20_40"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["20_40"]),
      "40_60": toFiniteNumber(source.retentionMarkPriceAdjustments?.["40_60"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["40_60"]),
      "60_80": toFiniteNumber(source.retentionMarkPriceAdjustments?.["60_80"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["60_80"]),
      "80_100": toFiniteNumber(source.retentionMarkPriceAdjustments?.["80_100"], DEFAULT_ASSUMPTIONS.retentionMarkPriceAdjustments["80_100"]),
   };
}

/** Fraction change to apply to the mark m² price, e.g. 0.10 for +10%. Missing retention => 0. */
export function getRetentionMarkPriceAdjustment(
   averageRetentionPercent: number | null | undefined,
   assumptions?: JPBAssumptions
): number {
   if (averageRetentionPercent === null || averageRetentionPercent === undefined || !Number.isFinite(averageRetentionPercent)) {
      return 0;
   }
   const key = getRetentionMarkPriceBandKey(averageRetentionPercent);
   return getRetentionMarkPriceAdjustments(assumptions)[key];
}

export function applyRetentionAdjustmentToSimulatedPrices(
   markPricePerM2: number,
   agriFraction: number,
   nonAgriRatio: number,
   adjustment: number
): { adjustedMarkPrice: number; otherPrice: number; estimatedTotal: number } {
   const adj = Number.isFinite(adjustment) ? adjustment : 0;
   const adjustedMarkPrice = markPricePerM2 * (1 + adj);
   const otherPrice = markPricePerM2 * nonAgriRatio;
   const estimatedTotal = adjustedMarkPrice * agriFraction + otherPrice * (1 - agriFraction);
   return { adjustedMarkPrice, otherPrice, estimatedTotal };
}

function getEffectiveMarkDenominator(markOverlapPct: number, delta: number): number {
   const alpha = markOverlapPct / 100;
   return alpha + (1 - alpha) * delta;
}

export function getWeightedValuesForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): JPBWeightedValue[] {
   if (!properties || properties.length === 0) return [];

   const delta = opts?.delta ?? getNonAgricultureToAgricultureRatio();
   const averageCalculationMethod = opts?.averageCalculationMethod ?? AverageCalculationMethod.WeightedByArea;
   const out: JPBWeightedValue[] = [];

   for (const p of properties) {
      if (!isValidPositiveNumber(p.area)) continue;

      if (series === "indexed") {
         if (isValidPositiveNumber(p.pricePerSquareMeter)) {
            out.push({
               value: p.pricePerSquareMeter,
               weight: getWeightForAverageMode(averageCalculationMethod, p.area)
            });
         }
         continue;
      }

      if (series === "original") {
         if (isValidPositiveNumber(p.originalPricePerSquareMeter)) {
            out.push({
               value: p.originalPricePerSquareMeter,
               weight: getWeightForAverageMode(averageCalculationMethod, p.area)
            });
         }
         continue;
      }

      const markOverlap = p.overlapArea?.markOverlap;
      if (!isValidNonNegativeNumber(markOverlap)) continue;
      if (!isValidPositiveNumber(p.pricePerSquareMeter)) continue;

      const denom = getEffectiveMarkDenominator(markOverlap, delta);
      if (!isValidPositiveNumber(denom)) continue;

      const effectivePrice = p.pricePerSquareMeter / denom;
      const effectiveWeight = getWeightForAverageMode(averageCalculationMethod, p.area * denom);

      if (isValidPositiveNumber(effectivePrice) && isValidPositiveNumber(effectiveWeight)) {
         out.push({ value: effectivePrice, weight: effectiveWeight });
      }
   }

   return out;
}

export function calculateWeightedMean(values: JPBWeightedValue[]): number | null {
   if (!values || values.length === 0) return null;
   let weightedSum = 0;
   let totalWeight = 0;
   for (const v of values) {
      if (!isValidPositiveNumber(v.value) || !isValidPositiveNumber(v.weight)) continue;
      weightedSum += v.value * v.weight;
      totalWeight += v.weight;
   }
   if (totalWeight <= 0) return null;
   return weightedSum / totalWeight;
}

export function calculateWeightedMeanForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): number | null {
   return calculateWeightedMean(getWeightedValuesForSeries(properties, series, opts));
}

function getWeightedQuantile(sorted: JPBWeightedValue[], quantile: number): number | null {
   if (!sorted || sorted.length === 0) return null;
   const totalWeight = sorted.reduce((sum, d) => sum + d.weight, 0);
   if (totalWeight <= 0) return null;

   const target = totalWeight * quantile;
   let cumulative = 0;
   for (const d of sorted) {
      cumulative += d.weight;
      if (cumulative >= target) return d.value;
   }
   return sorted[sorted.length - 1]?.value ?? null;
}

export function calculateWeightedStatisticsForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   method: StatisticsMethod,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): { lower: number; middle: number; upper: number } | null {
   const weightedValues = getWeightedValuesForSeries(properties, series, opts);
   if (weightedValues.length === 0) return null;

   const sorted = [...weightedValues].sort((a, b) => a.value - b.value);
   const mean = calculateWeightedMean(sorted);
   if (mean === null) return null;

   if (method === "quantile") {
      const q25 = getWeightedQuantile(sorted, 0.25);
      const q75 = getWeightedQuantile(sorted, 0.75);
      return {
         lower: q25 ?? mean,
         middle: mean,
         upper: q75 ?? mean,
      };
   }

   if (method === "mean") {
      let lowerSum = 0;
      let lowerWeight = 0;
      let upperSum = 0;
      let upperWeight = 0;

      for (const d of sorted) {
         if (d.value <= mean) {
            lowerSum += d.value * d.weight;
            lowerWeight += d.weight;
         }
         if (d.value >= mean) {
            upperSum += d.value * d.weight;
            upperWeight += d.weight;
         }
      }

      const lowerAvg = lowerWeight > 0 ? lowerSum / lowerWeight : mean;
      const upperAvg = upperWeight > 0 ? upperSum / upperWeight : mean;

      return { lower: lowerAvg, middle: mean, upper: upperAvg };
   }

   const totalWeight = sorted.reduce((sum, d) => sum + d.weight, 0);
   if (totalWeight <= 0) return { lower: mean, middle: mean, upper: mean };
   const variance =
      sorted.reduce((sum, d) => sum + d.weight * Math.pow(d.value - mean, 2), 0) / totalWeight;
   const stdDev = Math.sqrt(variance);
   const margin = 0.674 * stdDev;

   return { lower: mean - margin, middle: mean, upper: mean + margin };
}

export function getSortedValuesForSeries(
   properties: JPBReferenceProperty[],
   series: JPBStatisticsSeries,
   opts?: { delta?: number; averageCalculationMethod?: AverageCalculationMethod }
): number[] {
   if (!properties || properties.length === 0) return [];

   return getWeightedValuesForSeries(properties, series, opts)
      .map(v => v.value)
      .sort((a, b) => a - b);
}

export function getStatisticsLabels(method: StatisticsMethod): { lower: string; middle: string; upper: string } {
   if (method === "quantile") {
      return {
         lower: "25%",
         middle: Localization.getText("Mean"),
         upper: "75%",
      };
   }

   if (method === "mean") {
      return {
         lower: Localization.getText("Below mean avg"),
         middle: Localization.getText("Mean"),
         upper: Localization.getText("Above mean avg"),
      };
   }

   return {
      lower: Localization.getText("Lower"),
      middle: Localization.getText("Mean"),
      upper: Localization.getText("Upper"),
   };
}

export function calculateEffectiveMarkSquareMeterPrice(
   property: JPBReferenceProperty,
   delta?: number
): number | null {
   const resolvedDelta = delta ?? getNonAgricultureToAgricultureRatio();
   const markOverlap = property.overlapArea?.markOverlap;
   if (!isValidNonNegativeNumber(markOverlap)) return null;
   if (!isValidPositiveNumber(property.pricePerSquareMeter)) return null;

   const denom = getEffectiveMarkDenominator(markOverlap, resolvedDelta);
   if (!isValidPositiveNumber(denom)) return null;

   return property.pricePerSquareMeter / denom;
}

export function mapSoilTypeToGroup(jordtype: string | null | undefined): SoilQualityGroup | null {
   if (!jordtype) return null;

   const normalized = jordtype.toLowerCase().trim();

   if (normalized.includes('sandblandet lerjord')) {
      return SoilQualityGroup.JB_5_6;
   }

   if (normalized.includes('lerblandet sandjord')) {
      return SoilQualityGroup.JB_3_4;
   }

   if (normalized.includes('grovsandet jord') || normalized.includes('finsandet jord')) {
      return SoilQualityGroup.JB_1_2;
   }

   if (normalized.includes('siltjord')) {
      return SoilQualityGroup.JB_10;
   }

   if (
      (normalized.includes('lerjord') ||
         normalized.includes('svær lerjord') ||
         normalized.includes('meget svær lerjord')) &&
      !normalized.includes('sandblandet') &&
      !normalized.includes('lerblandet')
   ) {
      return SoilQualityGroup.JB_7_9;
   }

   if (normalized.includes('humusjord')) {
      return SoilQualityGroup.JB_11;
   }

   console.warn(`Unmapped soil type: "${jordtype}"`);
   return null;
}

/** Display names of the jordtyper. Which JB group each name belongs to comes from `mapSoilTypeToGroup`. */
export const JPB_SOIL_TYPE_NAMES = [
   "Grovsandet jord",
   "Finsandet jord",
   "Grov lerblandet sandjord",
   "Fin lerblandet sandjord",
   "Grov sandblandet lerjord",
   "Fin sandblandet lerjord",
   "Lerjord",
   "Svær lerjord",
   "Meget svær lerjord",
   "Siltjord",
   "Humusjord",
] as const;

/** Soil types in each current JB group, in `JPB_SOIL_TYPE_NAMES` order. */
export function getSoilTypesByGroup(): Record<SoilQualityGroup, string[]> {
   const grouped = {} as Record<SoilQualityGroup, string[]>;
   for (const group of SOIL_QUALITY_GROUPS) grouped[group] = [];
   for (const name of JPB_SOIL_TYPE_NAMES) {
      const group = mapSoilTypeToGroup(name);
      if (!group || grouped[group] === undefined) continue;
      grouped[group].push(name);
   }
   return grouped;
}

export function calculateSoilSpecificAgriculturalPrices(
   properties: JPBReferenceProperty[],
   delta?: number,
   soilFactorsByGroup?: JPBSoilFactorsByGroup,
   averageCalculationMethod: AverageCalculationMethod = AverageCalculationMethod.WeightedByArea
): { [soilGroup in SoilQualityGroup]?: number } | null {
   const resolvedAssumptions = getResolvedJPBAssumptions();
   const resolvedDelta = delta ?? getNonAgricultureToAgricultureRatio(resolvedAssumptions);
   const resolvedSoilFactors = soilFactorsByGroup ?? getSoilQualityFactorsByGroup(resolvedAssumptions);

   if (!properties || properties.length === 0) {
      return null;
   }

   const agriculturalPrices: { [propertyId: string]: number } = {};
   const agriculturalAreas: { [propertyId: string]: number } = {};
   const soilCompositionIndices: { [propertyId: string]: number } = {};

   for (const prop of properties) {
      const alpha = (prop.overlapArea?.markOverlap ?? 0) / 100;
      const P_total_per_m2 = prop.pricePerSquareMeter || 0;
      const A_total = prop.area || 0;

      if (alpha <= 0 || P_total_per_m2 <= 0 || A_total <= 0) {
         continue;
      }

      const denom = alpha + (1 - alpha) * resolvedDelta;
      if (denom <= 0) {
         continue;
      }

      const P_agri = P_total_per_m2 / denom;
      const A_agri = A_total * alpha;

      agriculturalPrices[prop.documentID] = P_agri;
      agriculturalAreas[prop.documentID] = A_agri;

      const jordbundskortOverlap = prop.overlapArea?.jordbundskortOverlap;
      if (!jordbundskortOverlap) {
         continue;
      }

      const totalPercentage = Object.values(jordbundskortOverlap).reduce(
         (sum, p) => sum + (typeof p === 'number' && p > 0 ? p : 0),
         0
      );

      const isFarmlandBased = Math.abs(totalPercentage - 100) < 10;
      const normalizationFactor = totalPercentage > 0 && !isFarmlandBased
         ? 100 / totalPercentage
         : 1.0;

      let Q_p = 0;
      for (const [soilType, percentage] of Object.entries(jordbundskortOverlap)) {
         if (typeof percentage !== 'number' || percentage <= 0) {
            continue;
         }

         const soilGroup = mapSoilTypeToGroup(soilType);
         if (!soilGroup || !SOIL_QUALITY_GROUPS.includes(soilGroup)) {
            continue;
         }

         const normalizedPercentage = percentage * normalizationFactor;
         const s_p_i = normalizedPercentage / 100;

         const q_i = resolvedSoilFactors[soilGroup];
         Q_p += s_p_i * q_i;
      }

      if (Q_p > 0) {
         soilCompositionIndices[prop.documentID] = Q_p;
      }
   }

   if (Object.keys(agriculturalPrices).length === 0 || Object.keys(soilCompositionIndices).length === 0) {
      return null;
   }

   let numerator = 0;
   let denominator = 0;

   for (const propId of Object.keys(agriculturalPrices)) {
      const P_agri = agriculturalPrices[propId];
      const A_agri = agriculturalAreas[propId];
      const Q_p = soilCompositionIndices[propId];

      if (!P_agri || !A_agri || !Q_p) {
         continue;
      }

      const weight = getWeightForAverageMode(averageCalculationMethod, A_agri);
      const contribution = weight * P_agri;
      numerator += contribution;
      denominator += weight * Q_p;
   }

   if (denominator === 0) {
      return null;
   }

   const P_base = numerator / denominator;

   const soilPrices: { [soilGroup in SoilQualityGroup]?: number } = {};

   for (const group of SOIL_QUALITY_GROUPS) {
      const q_i = resolvedSoilFactors[group];
      const P_i = P_base * q_i;
      soilPrices[group] = P_i;
   }

   return soilPrices;
}

export function calculateAreaPrice(
   agriFraction: number,
   soilComposition: { [soilGroup: string]: number },
   soilPrices: { [soilGroup: string]: number },
   delta?: number
): number {
   const resolvedDelta = delta ?? getNonAgricultureToAgricultureRatio();
   if (agriFraction <= 0) return 0;
   const totalSoilFraction = Object.values(soilComposition).reduce((a, b) => a + b, 0);
   if (totalSoilFraction <= 0) {
      throw new Error("Soil composition fractions must sum to > 0");
   }

   let P_agri = 0;
   for (const [soilGroup, fraction] of Object.entries(soilComposition)) {
      const price = soilPrices[soilGroup];
      if (price === undefined) {
         throw new Error(`Missing soil price for group ${soilGroup}`);
      }
      P_agri += fraction * price;
   }

   const P_total = P_agri * (agriFraction + (1 - agriFraction) * resolvedDelta);

   return P_total;
}

export function calculatePropertyEstimatedPrices(
   properties: JPBReferenceProperty[],
   soilPrices: { [soilGroup in SoilQualityGroup]?: number },
   delta?: number
): Array<JPBReferenceProperty & { estimatedPricePerSquareMeter: number | null }> {
   return properties.map(prop => {
      const agriFraction = (prop.overlapArea?.markOverlap ?? 0) / 100;
      const jordbundskortOverlap = prop.overlapArea?.jordbundskortOverlap;

      if (agriFraction <= 0 || !jordbundskortOverlap) {
         return { ...prop, estimatedPricePerSquareMeter: null };
      }

      const soilComposition: { [soilGroup: string]: number } = {};
      let totalMappedPercentage = 0;

      for (const [soilType, percentage] of Object.entries(jordbundskortOverlap)) {
         if (typeof percentage !== 'number' || percentage <= 0) {
            continue;
         }

         const soilGroup = mapSoilTypeToGroup(soilType);
         if (!soilGroup || !SOIL_QUALITY_GROUPS.includes(soilGroup)) {
            continue;
         }

         const fraction = percentage / 100;
         if (soilComposition[soilGroup]) {
            soilComposition[soilGroup] += fraction;
         } else {
            soilComposition[soilGroup] = fraction;
         }
         totalMappedPercentage += percentage;
      }

      if (totalMappedPercentage > 0) {
         const normalizationFactor = 100 / totalMappedPercentage;
         for (const group in soilComposition) {
            soilComposition[group] *= normalizationFactor;
         }
      }

      const hasAllPrices = Object.keys(soilComposition).every(
         group => soilPrices[group as SoilQualityGroup] !== undefined
      );

      if (!hasAllPrices || Object.keys(soilComposition).length === 0) {
         return { ...prop, estimatedPricePerSquareMeter: null };
      }

      try {
         const estimatedPrice = calculateAreaPrice(
            agriFraction,
            soilComposition,
            soilPrices as { [soilGroup: string]: number },
            delta
         );
         return { ...prop, estimatedPricePerSquareMeter: estimatedPrice };
      } catch (error) {
         console.warn(`Failed to calculate estimated price for property ${prop.documentID}:`, error);
         return { ...prop, estimatedPricePerSquareMeter: null };
      }
   });
}

function compareNullablePercents(aValue: number | null | undefined, bValue: number | null | undefined): number {
   if (aValue == null || !Number.isFinite(aValue)) {
      if (bValue == null || !Number.isFinite(bValue)) return 0;
      return 1;
   }
   if (bValue == null || !Number.isFinite(bValue)) return -1;
   return aValue - bValue;
}

export function getComparator(columnKey: string): ((a: JPBReferenceProperty, b: JPBReferenceProperty) => number) {
   switch (columnKey) {
      case 'documentID':
         return (a, b) => a.documentID.localeCompare(b.documentID);
      case 'area':
         return (a, b) => (a.area || 0) - (b.area || 0);
      case 'originalTotalPrice':
         return (a, b) => (a.originalTotalPrice || 0) - (b.originalTotalPrice || 0);
      case 'totalPrice':
         return (a, b) => (a.totalPrice || 0) - (b.totalPrice || 0);
      case 'pricePerSquareMeter':
         return (a, b) => (a.pricePerSquareMeter || 0) - (b.pricePerSquareMeter || 0);
      case 'transactionDate':
         return (a, b) => new Date(a.transactionDate).getTime() - new Date(b.transactionDate).getTime();
      case 'transactionType':
      case 'bfe_nr':
      case 'landbrugsnotering':
         return (a, b) => {
            const key = columnKey as keyof JPBReferenceProperty;
            return String(a[key] ?? "").localeCompare(String(b[key] ?? ""));
         };
      case 'error':
         return (a, b) => (a.error ?? 0) - (b.error ?? 0);
      case 'markOverlap':
         return (a, b) => compareNullablePercents(a.overlapArea?.markOverlap, b.overlapArea?.markOverlap);
      case 'markOmdrift':
      case 'markIkkeOmdrift':
      case 'markSection3':
         return (a, b) => {
            const field = JPB_MARK_SHARE_COLUMNS.find((column) => column.columnKey === columnKey)?.field;
            return compareNullablePercents(
               field ? a.overlapArea?.[field] : null,
               field ? b.overlapArea?.[field] : null,
            );
         };
      case 'effectiveMarkPrice':
         return (a, b) => {
            const aValue = calculateEffectiveMarkSquareMeterPrice(a);
            const bValue = calculateEffectiveMarkSquareMeterPrice(b);
            if (aValue === null && bValue === null) return 0;
            if (aValue === null) return 1;
            if (bValue === null) return -1;
            return aValue - bValue;
         };
      default:
         return () => 0;
   }
}


export function getCheckedItemsFromCurrentAssumptions(
   properties: JPBReferenceProperty[],
   customAssumptions: Partial<JPBAssumptions> | undefined,
   priceMode: PriceMode
): { checkedItems: Set<string>, assumptions: JPBAssumptions } {
   const assumptions = getResolvedJPBAssumptions(customAssumptions);
   const delta = getNonAgricultureToAgricultureRatio(assumptions);

   const checkedItems = new Set(
      properties
         .filter(prop => {
            const markOverlap = prop.overlapArea?.markOverlap ?? 0;
            const hasBuildingValue =
               (typeof prop.prisDriftsbygning === "number" && Number.isFinite(prop.prisDriftsbygning) && prop.prisDriftsbygning > 0) ||
               (typeof prop.prisStuehus === "number" && Number.isFinite(prop.prisStuehus) && prop.prisStuehus > 0);
            const thresholdM2 = assumptions.buildingAreaOkM2 ?? 0;
            const buildingAreaM2 = assumptions.buildingAreaOkUseAgriculturalArea
               ? prop.area * (markOverlap / 100)
               : prop.area;
            const buildingAreaOk = !hasBuildingValue || thresholdM2 <= 0 || buildingAreaM2 >= thresholdM2;

            const pricePerSquareMeterToCheck =
               priceMode === PriceMode.Landbrug
                  ? calculateEffectiveMarkSquareMeterPrice(prop, delta)
                  : prop.pricePerSquareMeter;

            const hasValidPrice =
               typeof pricePerSquareMeterToCheck === "number" &&
               Number.isFinite(pricePerSquareMeterToCheck);
            return !(
               (!hasValidPrice) ||
               (pricePerSquareMeterToCheck < assumptions.pricePerMeterMin) ||
               (pricePerSquareMeterToCheck > assumptions.pricePerMeterMax) ||
               (prop.area < assumptions.areaMin) ||
               (prop.area > assumptions.areaMax) ||
               (!assumptions.includeTransactionTypes.includes(prop.transactionType)) ||
               (!buildingAreaOk) ||
               (markOverlap <= assumptions.effectiveMarkOverlapThreshold) ||
               (shareExceedsCap(prop.overlapArea?.markIkkeOmdriftPct, assumptions.maxIkkeOmdriftPct)) ||
               (shareExceedsCap(prop.overlapArea?.markSection3Pct, assumptions.maxSection3Pct)) ||
               (assumptions.excludeBuildings && hasBuildingValue)
            );
         })
         .map(prop => prop.documentID)
   );
   return { checkedItems, assumptions };
}
